import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { isInputLocked } from './inputLock.js';
import { slideMove } from './slideMove.js';
import { virtualInput } from './virtualInput.js';
import { resolveMoveInput } from './joystickMath.js';
import { dir8FromVector } from './walkAnim.js';
import { characterScaleAtCentre } from './outdoorScale.mjs';
import { rideStep, blockedStop } from './rideMotion.mjs';
import { arrivalMove } from './arrivalMove.mjs';
import { applyUpBlock } from './upBlock.mjs';

// Manages keyboard input + Natalka movement with bounds.
// Y bounds keep Natalka below the money bar and above the bottom edge.
const NATALKA_SPEED = 220;       // px/s
// The rooms and yards are drawn in perspective: a screen pixel up/down covers more ground than a
// pixel left/right, so equal pixel speeds felt like sprinting whenever she walked "into" the scene.
// Vertical movement is scaled down to read as the same ground speed.
const VERTICAL_SPEED_FACTOR = 0.6;
const NATALKA_WIDTH = 160;
const NATALKA_HEIGHT = 240;

// True when a DOM text field (chat box, friend-name input, …) is focused — movement
// keys should type, not walk. Belt-and-suspenders alongside per-input stopPropagation.
function isTextInputFocused() {
  const ae = typeof document !== 'undefined' ? document.activeElement : null;
  return !!ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
}

export class WalkController {
  constructor(scene, natalka, options = {}) {
    this.scene = scene;
    this.natalka = natalka;
    virtualInput.wantStickFor(scene, this);   // phones show the joystick only where someone walks
    this.cursors = scene.input.keyboard.createCursorKeys();
    this.keys = scene.input.keyboard.addKeys({
      W: Phaser.Input.Keyboard.KeyCodes.W,
      A: Phaser.Input.Keyboard.KeyCodes.A,
      S: Phaser.Input.Keyboard.KeyCodes.S,
      D: Phaser.Input.Keyboard.KeyCodes.D,
    });

    // Speed may be a constant or a function (e.g. the bike's grease-dependent speed).
    this._speed = options.speed ?? NATALKA_SPEED;
    // Sprite height used for the floor offset (bike rider is shorter than Natalka).
    this._spriteHeight = options.spriteHeight ?? NATALKA_HEIGHT;
    // Called with the horizontal sign (-1/0/+1) each moving frame — used for flipX.
    this._onMoveX = options.onMoveX ?? null;
    // When false, this controller ignores input (the other controller is active).
    this.enabled = options.enabled ?? true;
    // Optional obstacle test: blocked(feetX, feetY) → true means the feet can't be there.
    // Movement resolves axis-separated (slides along edges). Default: no obstacles.
    this._blocked = options.blocked ?? null;
    this._onBroadcast = options.onBroadcast ?? null;
    // Programmatic walking (the farm's click-to-act queue): consulted only while no key or
    // joystick is held, so a player can always take over by touching the controls.
    this._autoVector = options.autoVector ?? null;
    this.manualMoving = false;
    this._mode = options.mode ?? 'walk';
    // The bike speeds up and rolls to a stop (rideMotion.mjs) instead of moving only while a key is held:
    // `speed` and `pushing` say how it is moving now (grease drains while speed > 0).
    this._inertia = !!options.inertia;
    this.speed = 0; this.pushing = false; this._rideDir = null; this._blockedDir = null;
    this._lastBroadcast = 0;
    this._wasMoving = false;
    this._announced = false;     // the arrival broadcast (arrivalMove.mjs), on the first frame
    this._facing = 'right';
    this._curDir8 = null; this._prevDir8 = null; this._curVec = null; this._prevVec = null; this._dirSince = 0;

    // Side bounds on the sprite centre. Sized for the 240-px indoor art by default; an
    // outdoor scene draws her at half that and passes its own `xBounds: [min, max]` so the
    // lawn right up to the edge of the art is reachable.
    this.boundsXMin = options.xBounds?.[0] ?? NATALKA_WIDTH / 2 + 10;
    this.boundsXMax = options.xBounds?.[1] ?? GAME_WIDTH - NATALKA_WIDTH / 2 - 10;
    const sceneHeight = GAME_HEIGHT;  // world units; scene.scale.* is the retina canvas size (2x)
    // The bottom bound is on the sprite's CENTRE. A scene with a real edge to its walkable
    // ground (the street's pavement stops at the kerb) pins it with `bottomY` and it stays
    // put; everywhere else it is "as low as the screen allows", which has to follow the
    // sprite's own height (a bike rider, or the half-size character outdoors) or a smaller
    // sprite stops walking with its feet well above the bottom of the screen.
    this._bottomY = Number.isFinite(options.bottomY) ? options.bottomY : null;
    this.boundsYMax = this._bottomY ?? (sceneHeight - this._spriteHeight / 2 + 30);

    // Floor model: either a constant Y (floorY option) or a polyline of points (floorPolyline option).
    // Polyline: array of [x, y] sorted by x; floorY at any X is linearly interpolated between segments.
    if (options.floorPolyline && options.floorPolyline.length >= 2) {
      this._floorPolyline = [...options.floorPolyline].sort((a, b) => a[0] - b[0]);
      this.boundsYMin = null;  // dynamic — overridden by getFloorYAt()
    } else {
      this._floorPolyline = null;
      this.boundsYMin = (options.floorY !== undefined) ? options.floorY : Math.floor(sceneHeight * 0.66);
    }
  }

  // The sprite this controller moves changed size (a bike rider swapped for a taller/shorter
  // one). The floor offset and the bottom bound are both measured from the sprite's centre,
  // so both have to follow.
  setSpriteHeight(h) {
    if (!Number.isFinite(h) || h <= 0) return;
    this._spriteHeight = h;
    this.boundsYMax = this._bottomY ?? (GAME_HEIGHT - h / 2 + 30);
  }

  // Swaps the floor polyline at runtime (e.g. a backyard upgrade level moves the
  // garage/altana footprint). Points need not be pre-sorted by the caller.
  setFloorPolyline(pts) {
    this._floorPolyline = [...pts].sort((a, b) => a[0] - b[0]);
    this.boundsYMin = null;
  }

  getFloorYAt(x) {
    if (!this._floorPolyline) return this.boundsYMin;
    const pts = this._floorPolyline;
    if (x <= pts[0][0]) return pts[0][1];
    if (x >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
    for (let i = 0; i < pts.length - 1; i++) {
      const [x1, y1] = pts[i];
      const [x2, y2] = pts[i + 1];
      if (x >= x1 && x <= x2) {
        const t = (x - x1) / (x2 - x1);
        return y1 + (y2 - y1) * t;
      }
    }
    return pts[0][1]; // fallback
  }

  // A diagonal walk (odd dir8) whose one key was released < 150 ms ago: the remaining key's straight
  // direction is only the release tail, not a new direction.
  _graceActive() {
    const p = this._prevDir8, c = this._curDir8;
    if (p == null || c == null || p % 2 !== 1 || c % 2 !== 0) return false;
    const adjacent = (c - p + 8) % 8 === 1 || (p - c + 8) % 8 === 1;   // the straight direction is one of the diagonal's two keys
    return adjacent && performance.now() - this._dirSince < 150;
  }

  // The bike's motion as it rolls now, and set again in the next scene (rideMotion.carriedRide).
  get rideDir() { return this._rideDir; }
  get topSpeed() { return this._currentSpeed(); }
  carryRide({ speed, dir }) { this.speed = speed; this._rideDir = dir; }

  _currentSpeed() {
    const base = (typeof this._speed === 'function') ? this._speed() : this._speed;
    // Scenes drawn in perspective (yard, backyard) shrink her with distance; her ground speed
    // shrinks by the same ratio so a far-away walk does not race across the screen.
    // Same resolution as fitCharacter: outdoor scenes (worldScale < 1) foreshorten with the
    // scene's profile or the street's default; indoors this is exactly 1.
    const ws = this.scene.worldScale ?? 1;
    if (!this.natalka || (ws === 1 && !this.scene.perspective)) return base;
    return base * (characterScaleAtCentre(this.scene, this.natalka.y) / ws);
  }

  // Is the player walking upward (up, up-left, up-right) this frame? A store asks as it hands over to the
  // street, which then ignores up until they walk another way (`blockUp`, upBlock.mjs).
  get walkingUp() { return this.manualMoving && (this._curVec?.dy ?? 0) < 0; }

  update(deltaMs) {
    if (!this.enabled) return;
    if (!this._announced) {
      this._announced = true;
      const p = arrivalMove({ sprite: this.natalka, facing: this._facing, mode: this._mode, canBroadcast: !!this._onBroadcast });
      if (p) this._onBroadcast(p);
    }
    this.manualMoving = false;
    if (isInputLocked(this.scene) || isTextInputFocused() || (this.natalka && this.natalka.visible === false)) {
      // a modal is open / typing in a chat field / seated — she stays put, and so does her walk cycle
      this.natalka?.setMotion?.(0, 0);
      if (this._inertia && this.speed > 0) { this.speed = 0; this.pushing = false; this.natalka?.setRide?.({ speed: 0, pedalling: false }); }
      return;
    }
    const dt = deltaMs / 1000;
    const kbDx = ((this.cursors.left.isDown || this.keys.A.isDown) ? -1 : 0)
               + ((this.cursors.right.isDown || this.keys.D.isDown) ? 1 : 0);
    const kbDy = ((this.cursors.up.isDown || this.keys.W.isDown) ? -1 : 0)
               + ((this.cursors.down.isDown || this.keys.S.isDown) ? 1 : 0);
    const manual = resolveMoveInput(kbDx, kbDy, virtualInput.vector);
    let { dx, dy } = manual;
    if (this.blockUp) ({ blocked: this.blockUp, dx, dy } = applyUpBlock(true, dx, dy));   // see upBlock.mjs
    this.manualMoving = dx !== 0 || dy !== 0;
    if (!this.manualMoving && this._autoVector) {
      const auto = this._autoVector();
      if (auto && (auto.dx !== 0 || auto.dy !== 0)) { dx = auto.dx; dy = auto.dy; }
    }
    if (this._inertia) { this._updateRide(dt, dx, dy); return; }
    if (dx === 0 && dy === 0) {
      // Two keys are never released in the same frame: a diagonal walk ends with a few ms of a single one of
      // its keys. That tail is ignored (see the grace below), so she rests in the diagonal she was walking.
      if (this._graceActive()) this.natalka.setFacing8?.(this._prevDir8);
      this._curDir8 = null; this._prevDir8 = null;
      this.natalka.setMotion?.(0, 0);   // animated-walk prototype (NatalkaSprite); no-op on other sprites
    }
    if (dx === 0 && dy === 0) {
      if (this._wasMoving && this._onBroadcast) {
        this._wasMoving = false;
        this._onBroadcast({ x: this.natalka.x, y: this.natalka.y, facing: this._facing, mode: this._mode, dir: this.natalka.facing8 });
      }
      return;
    }
    if (dx !== 0) this._onMoveX?.(Math.sign(dx));
    const d8 = dir8FromVector(dx, dy);
    if (d8 !== this._curDir8) { this._prevDir8 = this._curDir8 ?? null; this._prevVec = this._curVec ?? null; this._curDir8 = d8; this._curVec = { dx, dy }; this._dirSince = performance.now(); }
    const len = Math.hypot(dx, dy);
    const speed = this._currentSpeed();
    const h2 = this._spriteHeight / 2;
    // Candidate position clamped to the X bounds + floor (polyline points are FEET
    // positions, so the sprite centre sits h2 above the floor line).
    const candX = Phaser.Math.Clamp(this.natalka.x + (dx / len) * speed * dt, this.boundsXMin, this.boundsXMax);
    const minY = this._floorPolyline ? this.getFloorYAt(this.natalka.x) - h2 : this.boundsYMin;
    const candCenterY = Phaser.Math.Clamp(this.natalka.y + (dy / len) * speed * VERTICAL_SPEED_FACTOR * dt, minY, this.boundsYMax);

    const prevX = this.natalka.x, prevY = this.natalka.y;
    if (this._blocked) {
      // Resolve against obstacles on FEET coordinates, axis-separated → slide along edges.
      const curFeetY = this.natalka.y + h2;
      const res = slideMove(this.natalka.x, curFeetY, candX - this.natalka.x, candCenterY - this.natalka.y, this._blocked);
      this.natalka.x = res.x;
      this.natalka.y = res.y - h2;
    } else {
      this.natalka.x = candX;
      this.natalka.y = candCenterY;
    }
    // Animate only when she actually moved (a key held against a wall or a bound only turns her). During the
    // grace tail of a diagonal walk the animation keeps the diagonal, so releasing the two keys a few ms apart
    // never flashes a straight frame.
    if (this.natalka.setMotion) {
      const moved = Math.abs(this.natalka.x - prevX) > 0.01 || Math.abs(this.natalka.y - prevY) > 0.01;
      const anim = this._graceActive() ? this._prevVec : { dx, dy };
      if (moved) this.natalka.setMotion(anim.dx, anim.dy);
      else { this.natalka.setFacing8?.(dir8FromVector(anim.dx, anim.dy)); this.natalka.setMotion(0, 0); }
    }

    if (this._onBroadcast) {
      if (dx < 0) this._facing = 'left'; else if (dx > 0) this._facing = 'right';
      const now = performance.now();
      if (now - this._lastBroadcast >= 100) {       // ~10Hz
        this._lastBroadcast = now;
        this._onBroadcast({ x: this.natalka.x, y: this.natalka.y, facing: this._facing, mode: this._mode, dir: this.natalka.facing8 });
      }
      this._wasMoving = true;
    }
  }

  // The bike's tick: rideStep speeds it up toward the top speed while a direction is held and rolls it on along
  // its last direction once let go; a move a wall or a bound swallows stops it. Same clamps, slideMove,
  // perspective speed and vertical factor as the walk. The sprite hears the speed and whether the rider pedals.
  _updateRide(dt, dx, dy) {
    const input = dx !== 0 || dy !== 0 ? { dx, dy } : null;
    if (input) {
      if (dx !== 0) this._onMoveX?.(Math.sign(dx));
      const d8 = dir8FromVector(dx, dy);
      if (d8 !== this._curDir8) { this._prevDir8 = this._curDir8 ?? null; this._prevVec = this._curVec ?? null; this._curDir8 = d8; this._curVec = { dx, dy }; this._dirSince = performance.now(); }
    } else if (this._curDir8 != null) {
      // a diagonal let go a few ms apart rolls on along the diagonal, not the last key's straight tail
      if (this._graceActive() && this._prevVec) {
        const l = Math.hypot(this._prevVec.dx, this._prevVec.dy);
        this._rideDir = { dx: this._prevVec.dx / l, dy: this._prevVec.dy / l };
        this.natalka.setFacing8?.(this._prevDir8);
      }
      this._curDir8 = null; this._prevDir8 = null;
    }
    if (!input) this._blockedDir = null;
    const st = rideStep({ speed: this.speed, dir: this._rideDir, input, top: this._currentSpeed(), dt, blockedDir: this._blockedDir });
    this.speed = st.speed; this._rideDir = st.dir; this.pushing = st.pushing;

    let moved = false;
    if (this.speed > 0 && this._rideDir) {
      const h2 = this._spriteHeight / 2;
      const { dx: ux, dy: uy } = this._rideDir;
      const candX = Phaser.Math.Clamp(this.natalka.x + ux * this.speed * dt, this.boundsXMin, this.boundsXMax);
      const minY = this._floorPolyline ? this.getFloorYAt(this.natalka.x) - h2 : this.boundsYMin;
      const candCenterY = Phaser.Math.Clamp(this.natalka.y + uy * this.speed * VERTICAL_SPEED_FACTOR * dt, minY, this.boundsYMax);
      const prevX = this.natalka.x, prevY = this.natalka.y;
      if (this._blocked) {
        const res = slideMove(this.natalka.x, prevY + h2, candX - prevX, candCenterY - prevY, this._blocked);
        this.natalka.x = res.x;
        this.natalka.y = res.y - h2;
      } else {
        this.natalka.x = candX;
        this.natalka.y = candCenterY;
      }
      const intended = Math.hypot(ux * this.speed * dt, uy * this.speed * VERTICAL_SPEED_FACTOR * dt);
      const went = Math.hypot(this.natalka.x - prevX, this.natalka.y - prevY);
      moved = went > 0.01;
      if (blockedStop(went, intended)) { this.speed = 0; moved = false; if (input) this._blockedDir = this._rideDir; }
    }
    this.natalka.setRide?.({ speed: this.speed, pedalling: this.pushing });
    if (moved) {
      const anim = input ? (this._graceActive() ? this._prevVec : input) : this._rideDir;
      this.natalka.setMotion?.(anim.dx, anim.dy);
    } else {
      if (input) this.natalka.setFacing8?.(dir8FromVector(dx, dy));   // pushing against a wall only turns it
      this.natalka.setMotion?.(0, 0);
    }

    if (!this._onBroadcast) return;
    if (this.speed > 0) {
      const dirX = this._rideDir?.dx ?? 0;
      if (dirX < 0) this._facing = 'left'; else if (dirX > 0) this._facing = 'right';
      const now = performance.now();
      if (now - this._lastBroadcast >= 100) {       // ~10Hz, coasting too
        this._lastBroadcast = now;
        this._onBroadcast({ x: this.natalka.x, y: this.natalka.y, facing: this._facing, mode: this._mode, dir: this.natalka.facing8 });
      }
      this._wasMoving = true;
    } else if (this._wasMoving) {                    // the idle broadcast: once the bike has stopped, not on key-up
      this._wasMoving = false;
      this._onBroadcast({ x: this.natalka.x, y: this.natalka.y, facing: this._facing, mode: this._mode, dir: this.natalka.facing8 });
    }
  }
}

export const NATALKA_SIZE = { w: NATALKA_WIDTH, h: NATALKA_HEIGHT };
